import { toString, includes, isEmpty, forEach, toNumber, get, head, indexOf, isBoolean, isNumber } from 'lodash';

// local dependencies
import { DATA_TYPES, DATA_VALIDATIONS } from 'constants/common';

/**
 * @desc A model to construct form field object.
 * @example
 * import { FormControl } from 'models/FormControl';
 */
export class FormControl {
    constructor( { name, type, validations } = {} ) {

        // field `string` name
        this.name = toString( name );

        // field data type
        this.type = includes( DATA_TYPES, type ) ? type : DATA_TYPES.TEXT;

        // validations object
        this.validations = isEmpty( validations ) ? [] : validations;
    }

    /**
     * @desc Run validation of the field.
     * @param value - field value
     * @returns Returns the unsatisfied rule back, else `null`.
     */
    validate( { value, field } = {} ) {
        log.debug( 'FormControl.validate()' );
        let result = null;
        if( ! isEmpty( this.validations ) ) {
            forEach( this.validations, rule => {
                if( ! this.validateRule( { rule, value, field } ) ) {
                    result = rule; // update `result` value with `rule` to indicate an error
                    return false; // break the `forEach` loop
                }
            } );
        }

        return result;
    }

    /**
     * @desc Validates an individual rule from `this.validations`.
     * @returns Returns `true` if rule satisfies.
     */
    validateRule( { rule = {}, value = null, field } = {} ) {
        log.debug( 'FormControl.validateRule()' );

        switch( rule.name ) {

            // required field
            case DATA_VALIDATIONS.REQUIRED: {

                if( isBoolean( value ) || isNumber( value ) || get( field, 'config.hide', false ) ) {
                    return true;
                } else {
                    return ! isEmpty( value );
                }
            }

            // maximum length of the value
            case DATA_VALIDATIONS.MAX_LENGTH: {
                return value.length <= rule.maxLength;      // Form-control - validate.js
            }

            // US Zip code
            case DATA_VALIDATIONS.ZIPCODE: {  //if validation name === zipCode then performs zipCode Validation

                if( get( field, 'config.hide', false ) ) {
                    return true;
                } else {
                    return /\b\d{5}\b/g.test( value );
                }
            }
            
            // US Phone number
            case DATA_VALIDATIONS.US_PHONE: {
                return isEmpty( value ) || ( /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/.test( value ) );
            }

            // time
            case DATA_VALIDATIONS.TIME: {

                if( get( field, 'config.hide', false ) ) {
                    return true;
                } else {

                    if( '24' === get( field, 'config.format' ) ) {
                        return ( /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/i.test( value ) );
                    }
                    
                    // return ( /(((0[1-9])|(1[0-2])):([0-5])(0|5)\s(A|P|a|p)(M|m))/.test( value ) );
    
                    return ( /\b((1[0-2]|0?[1-9]):([0-5][0-9]) ([AaPp][Mm]))/i.test( value ) );
                }

                
            }
            
            // value equal to
            case DATA_VALIDATIONS.VALUE_ET: {
                return toNumber( rule.value  ) === toNumber( value  );
            }

            // value greater than
            case DATA_VALIDATIONS.VALUE_GT: {
                return toNumber( rule.value  ) < toNumber( value  );
            }

            // value greater than equal to
            case DATA_VALIDATIONS.VALUE_GTE: {
                return toNumber( rule.value  ) <= toNumber( value  );
            }

            // value less than
            case DATA_VALIDATIONS.VALUE_LT: {
                return toNumber( rule.value  ) > toNumber( value  );
            }

            // value less than equal to
            case DATA_VALIDATIONS.VALUE_LTE: {
                return toNumber( rule.value  ) >= toNumber( value  );
            }

            // required field
            case DATA_VALIDATIONS.NOT_EMPTY: {
                return ! isEmpty( value );
            }

            // email format
            case DATA_VALIDATIONS.EMAIL: {

                if( isEmpty( value ) ) {
                    return true;
                }
                
                const isValid = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test( value );
                return isValid;
            }

            // alphanumeric only
            case DATA_VALIDATIONS.ALPHANUMERIC: {
                return /^[0-9a-zA-Z]+$|^$/.test( value );  // either alphanumeric or empty
            }

            // exact length of the value
            case DATA_VALIDATIONS.EXACT_LENGTH: {
                return value.length === rule.exactLength;
            }

            // exact length of the value
            case DATA_VALIDATIONS.TYPE_NUMBERS: {
                return /^[0-9]*$/g.test( value );
            }

            // return `true` for unspecified rule
            default: {
                return true;
            }
        }
    }

    /**
     * @desc validateFields updates the fields property based on values
     * @returns Returns fields list
     */
    validateFields( { field, value = null, fields } = {} ) {
        log.debug( 'FormControl.validateFields()' );

        if( isEmpty( get( field, 'config.fieldRules', [] ) ) ) {
            return null;
        }

        forEach( field.config.fieldRules, rule => {

            switch( rule.name ) {

                // to handle visibility, based on value
                case 'handleVisibility': {
    
                    forEach( rule.fields, _field => {
                        const selectedValueIndex = indexOf( rule.checkValue, value );
                        fields[ _field ].config[ `${ rule.property }` ] = rule.updateValue[ selectedValueIndex ];
                    } );

                    break;
                }

                case 'handleSingleVisibility': {

                    forEach( rule.fields, _field => {
                        const checkValue = head( rule.checkValue );

                        if( isEmpty( checkValue ) || includes( rule.checkValue, value ) ) {

                            fields[ _field ].config[ `${ rule.property }` ] = head( rule.updateValue );

                        } else {

                            fields[ _field ].config[ `${ rule.property }` ] = ! head( rule.updateValue );

                        }
                    } );

                    break;
                }

                case 'updateValues': {
                    break; //don't change this case container handles the value updates in its state
                }
            }

        } );
        
        return fields;
    }
}
